import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import UsageHistory from './UsageHistory';

const Auth = ({ onLogin, onLogout, isAuthenticated, user }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'usage'
  const navigate = useNavigate(); // Initialize navigate

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store tokens
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('token_expires', Date.now() + (data.expires_in * 1000));
        onLogin(data);
        navigate('/home'); // Redirect to home on successful login
      } else {
        setMessage(data.detail || 'An error occurred');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires');
    onLogout();
  };

  const generateApiKey = async () => {
    setApiKeyLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/generate-api-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('API key generated successfully!');
        // Refresh user data to show new API key
        window.location.reload();
      } else {
        setMessage(data.detail || 'Failed to generate API key');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const revokeApiKey = async () => {
    if (!window.confirm('Are you sure you want to revoke your API key? This action cannot be undone.')) {
      return;
    }

    setApiKeyLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/revoke-api-key`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('API key revoked successfully!');
        // Refresh user data to show updated API key
        window.location.reload();
      } else {
        setMessage(data.detail || 'Failed to revoke API key');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setApiKeyLoading(false);
    }
  };


  return (
    <div style={{ margin: '0 auto', padding: '20px', overflowX: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {/* Replace ternary with logical AND for conditional rendering */}
      {!isAuthenticated && (
        <div style={{ maxWidth: '350px', width: '100%', margin: '0 auto', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '32px 24px' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '24px' }}>Login</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '15px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '15px', boxSizing: 'border-box' }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '16px',
                marginBottom: '10px'
              }}
            >
              {loading ? 'Loading...' : 'Login'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '18px', fontSize: '15px', color: '#666' }}>
            Contact your administrator to create an account.
          </p>
          
          {/* Link to Digipin Finder */}
          <div style={{ textAlign: 'center', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
              Try our free Digipin Finder tool
            </p>
            <Link 
              to="/digipin-finder" 
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#218838';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#28a745';
              }}
            >
              Try Digipin Finder
            </Link>
          </div>
          {message && (
            <div style={{ 
              marginTop: '12px', 
              padding: '10px', 
              backgroundColor: message.includes('error') ? '#f8d7da' : '#d4edda',
              color: message.includes('error') ? '#721c24' : '#155724',
              borderRadius: '4px',
              fontSize: '15px'
            }}>
              {message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Auth; 